import { FIND_MISSION_PAYROLL_BREAKDOWN } from "../../graphql/timesheet";
import {
    buildMission,
    buildMissionInfo,
    buildPayrollBreakdown,
    buildTimesheet,
    buildUser,
    buildWorkerInfo,
} from "../factories";
import { MockedAuthManager } from "../../contexts/AuthManager";
import { MockedProvider } from "@apollo/client/testing/react";
import React, { useMemo } from "react";
import {
    PayrollBreakdownStatus,
    PayrollSourceType,
    ProfileType,
    TimeSheetState,
} from "../../__generated__/graphql";
import type { Meta, StoryObj } from "@storybook/react";
import PayrollBreakdownListScreen from "../../screens/user/PayrollBreakdownListScreen";
import { navigatorLogger } from "../navigator-mock";
import { GET_MISSION } from "../../graphql/core";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";

const MOCKS = [
    {
        request: {
            query: FIND_MISSION_PAYROLL_BREAKDOWN,
            variables: {
                missionId: "001",
            },
        },
        result: () => ({
            data: {
                findMissionPayrollBreakdown: {
                    count: 4,
                    elements: [
                        buildPayrollBreakdown(new Date(2024, 0, 1), {
                            timesheets: [
                                buildTimesheet({
                                    status: TimeSheetState.Accepted,
                                }),
                                buildTimesheet({
                                    status: TimeSheetState.Submitted,
                                }),
                                buildTimesheet({
                                    status: TimeSheetState.Accepted,
                                }),
                                buildTimesheet({
                                    status: TimeSheetState.Accepted,
                                }),
                            ],
                        }),
                        buildPayrollBreakdown(new Date(2024, 0, 1), {
                            missionInfo: buildMissionInfo({
                                payrollSource: PayrollSourceType.ManualClient,
                            }),
                        }),
                        buildPayrollBreakdown(new Date(2024, 1, 1), {
                            timesheets: [
                                buildTimesheet({
                                    status: TimeSheetState.Open,
                                }),
                                buildTimesheet({
                                    status: TimeSheetState.Submitted,
                                }),
                                buildTimesheet({
                                    status: TimeSheetState.Accepted,
                                }),
                                buildTimesheet({
                                    status: TimeSheetState.Rejected,
                                }),
                            ],
                        }),
                        buildPayrollBreakdown(new Date(2024, 2, 1), {
                            timesheets: [
                                buildTimesheet({
                                    status: TimeSheetState.Open,
                                }),
                                buildTimesheet({
                                    status: TimeSheetState.Submitted,
                                }),
                                buildTimesheet({
                                    status: TimeSheetState.Submitted,
                                }),
                                buildTimesheet({
                                    status: TimeSheetState.Submitted,
                                }),
                            ],
                        }),
                        buildPayrollBreakdown(new Date(2024, 3, 1), {
                            status: PayrollBreakdownStatus.Submitted,
                        }),
                        buildPayrollBreakdown(new Date(2024, 3, 1), {
                            status: PayrollBreakdownStatus.Submitted,
                            missionInfo: buildMissionInfo({
                                payrollSource: PayrollSourceType.ManualClient,
                            }),
                        }),
                    ],
                    pageInfo: {
                        currentOffset: 0,
                        previousOffset: -1,
                        nextOffset: -1,
                        hasNextPage: false,
                        hasPreviousPage: false,
                        limit: 100,
                    },
                },
            },
        }),
    },
    {
        maxUsageCount: Number.POSITIVE_INFINITY,
        request: {
            query: GET_MISSION,
            variables: () => true,
        },
        result: () => {
            return {
                data: {
                    getMission: buildMission(),
                },
            };
        },
    },
];

const StoryComponent = ({ profileType }: { profileType: ProfileType }) => {
    const user = useMemo(
        () =>
            buildUser({
                workerInfo: buildWorkerInfo(),
            }),
        [],
    );

    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={profileType}>
                <MockedProvider mocks={MOCKS}>
                    <PayrollBreakdownListScreen
                        /* @ts-expect-error Stack Component need a complex navigation handler */
                        route={{ params: { missionId: "001" } }}
                        /* @ts-expect-error Stack Component need a complex navigation handler */
                        navigation={navigatorLogger}
                    />
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "Screens/PayrollBreakdownList",
    component: StoryComponent,
    argTypes: {
        profileType: {
            options: Object.values(ProfileType),
            control: {
                type: "select",
            },
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Client: Story = {
    args: {
        profileType: ProfileType.Client,
    },
};

export const Worker: Story = {
    args: {
        profileType: ProfileType.Worker,
    },
};
